'use client'

import { useEffect, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'

type HorizontalScrollControlsProps = {
  scrollRef: React.RefObject<HTMLDivElement>
  className?: string
  /** Hide the buttons when every item is already visible in the scroller. */
  hideWhenFits?: boolean
}

/**
 * Previous and next buttons for a horizontal snap scroller.
 */
export const HorizontalScrollControls = ({
  scrollRef,
  className,
  hideWhenFits = false,
}: HorizontalScrollControlsProps) => {
  const [overflows, setOverflows] = useState(!hideWhenFits)

  useEffect(() => {
    if (!hideWhenFits) return

    const el = scrollRef.current
    if (!el) return

    const update = () => {
      setOverflows(el.scrollWidth > el.clientWidth + 1)
    }

    const observer = new ResizeObserver(update)
    observer.observe(el)
    for (const child of el.children) {
      observer.observe(child)
    }

    return () => observer.disconnect()
  }, [hideWhenFits, scrollRef])

  if (hideWhenFits && !overflows) return null

  const scrollByCard = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return

    const firstCard = scrollRef.current.querySelector('[class*="snap-start"]') as HTMLElement
    if (!firstCard) return

    const gap = 16 // gap-4 = 1rem = 16px
    const scrollAmount = firstCard.offsetWidth + gap

    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    })
  }

  const scrollLeft = () => scrollByCard('left')
  const scrollRight = () => scrollByCard('right')

  return (
    <div className={cn('flex w-full gap-1', className)}>
      <Button
        variant="iconOutline"
        customIcon={<ChevronLeftIcon className="size-5 stroke-2" />}
        onClick={scrollLeft}
        aria-label="Scroll to previous item"
      />
      <Button
        variant="iconOutline"
        customIcon={<ChevronRightIcon className="size-5 stroke-2" />}
        onClick={scrollRight}
        aria-label="Scroll to next item"
      />
    </div>
  )
}
